import BaseTemperatureChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/BaseTemperatureChannel/BaseTemperatureChannel';
import ChannelGroupType from '@avtechsoftware/ra-javascript-utilities/src/types/ChannelGroupType';
import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import AppTypes from '@avtechsoftware/ra-javascript-utilities/src/types/AppTypes';
import ChannelType from '@avtechsoftware/ra-javascript-utilities/src/types/ChannelType';
import AirSpeedChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/AirSpeedChannel/AirSpeedChannel';
import AirFlowChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/AirFlowChannel/AirFlowChannel';
import {
  convertCubicMetersPerHourToCubicFeetPerMinute, convertToCubicMetersPerHour, convertToFeetPerMin,
  convertToMetersPerSecond,
} from '@avtechsoftware/ra-javascript-utilities/src/utilities/conversions/conversions';
import { adapterChannelGroupIndexTypes } from './types/AdapterChannelGroupIndexTypes';
import { getMaxComponentLabel } from './alertDisplayUtils';

export const generateChannelKey = (
  deviceMacAddress,
  sensorMacAddress,
  channelGroupType,
  channelGroupIndex,
  channelType,
  channelIndex,
) => `${deviceMacAddress}_${sensorMacAddress}_${channelGroupType}_${channelGroupIndex}_${channelType}_${channelIndex}`;

// eslint-disable-next-line import/prefer-default-export
export const parseChannelKey = (channelKey) => {
  if (!channelKey || typeof channelKey !== 'string') {
    // eslint-disable-next-line no-console
    console.warn('Invalid channelKey provided:', channelKey);
    return {
      deviceMacAddress: -1,
      sensorMacAddress: -1,
      channelGroupType: -1,
      channelGroupIndex: -1,
      channelType: -1,
      channelIndex: -1,
    };
  }

  const [
    deviceMacAddress = -1,
    sensorMacAddress = -1,
    channelGroupType = -1,
    channelGroupIndex = -1,
    channelType = -1,
    channelIndex = -1,
  ] = channelKey.split('_').map((value, index) => {
    if (index >= 2) {
      return parseInt(value, 10);
    }
    return value;
  });

  return {
    deviceMacAddress,
    sensorMacAddress,
    channelGroupType,
    channelGroupIndex,
    channelType,
    channelIndex,
  };
};

export const parseChannelGroupPartialKey = (channelGroupPartialKey) => {
  if (!channelGroupPartialKey || typeof channelGroupPartialKey !== 'string') {
    // eslint-disable-next-line no-console
    console.warn('Invalid channelGroupPartialKey provided:', channelGroupPartialKey);
    return {
      channelGroupType: -1,
      channelGroupIndex: -1,
    };
  }

  const [
    channelGroupType = -1,
    channelGroupIndex = -1,
  ] = channelGroupPartialKey.split('_').map((value) => parseInt(value, 10));

  return {
    channelGroupType,
    channelGroupIndex,
  };
};

export const parseChannelPartialKey = (channelPartialKey) => {
  if (!channelPartialKey || typeof channelPartialKey !== 'string') {
    // eslint-disable-next-line no-console
    console.warn('Invalid channelPartialKey provided:', channelPartialKey);
    return {
      channelType: -1,
      channelIndex: -1,
    };
  }

  const [
    channelType = -1,
    channelIndex = -1,
  ] = channelPartialKey.split('_').map((value) => parseInt(value, 10));

  return {
    channelType,
    channelIndex,
  };
};

export const normalizeAdjustFromDisplay = (adjust) => Math.round(adjust * 100) / 100;

export const normalizeAdjustToDisplay = (adjust) => Math.round(adjust * 100) / 100;

export const adjustTemperatureFromDisplay = (adjust, scale = 'C') => {
  if (scale === 'F') {
    return Math.round(((adjust * 5) / 9) * 100) / 100;
  }
  return normalizeAdjustFromDisplay(adjust);
};

export const adjustTemperatureToDisplay = (adjust, scale = 'C') => {
  if (scale === 'F') {
    return Math.round(((adjust * 9) / 5) * 100) / 100;
  }
  return normalizeAdjustToDisplay(adjust);
};

export const adjustAirSpeedFromDisplay = (adjust, scale = 'm/s') => (
  normalizeAdjustFromDisplay(convertToMetersPerSecond(adjust, scale))
);

export const adjustAirSpeedToDisplay = (adjust, scale = 'm/s') => (
  normalizeAdjustToDisplay(convertToFeetPerMin(adjust, scale))
);

export const adjustAirFlowFromDisplay = (adjust, scale = 'm/s') => (
  normalizeAdjustFromDisplay(convertToCubicMetersPerHour(adjust, scale === 'f/m'))
);

export const adjustAirFlowToDisplay = (adjust, scale = 'm/s') => (
  scale === 'f/m'
    ? normalizeAdjustToDisplay(convertCubicMetersPerHourToCubicFeetPerMinute(adjust))
    : normalizeAdjustToDisplay(adjust)
);

export const adjustFromDisplay = (channelInstance, adjust, scaleOptions = {}) => {
  if (channelInstance instanceof BaseTemperatureChannel) {
    return adjustTemperatureFromDisplay(adjust, scaleOptions.temperatureScale);
  }
  if (channelInstance instanceof AirSpeedChannel) {
    return adjustAirSpeedFromDisplay(adjust, scaleOptions.airSpeedScale);
  }
  if (channelInstance instanceof AirFlowChannel) {
    return adjustAirFlowFromDisplay(adjust, scaleOptions.airSpeedScale);
  }
  return normalizeAdjustFromDisplay(adjust);
};

export const adjustToDisplay = (channelInstance, adjust, scaleOptions = {}) => {
  if (channelInstance instanceof BaseTemperatureChannel) {
    return adjustTemperatureToDisplay(adjust, scaleOptions.temperatureScale);
  }
  if (channelInstance instanceof AirSpeedChannel) {
    return adjustAirSpeedToDisplay(adjust, scaleOptions.airSpeedScale);
  }
  if (channelInstance instanceof AirFlowChannel) {
    return adjustAirFlowToDisplay(adjust, scaleOptions.airSpeedScale);
  }
  return normalizeAdjustToDisplay(adjust);
};

export const findSensorWithInternalTempChannelGroup = (sensors) => {
  if (!sensors || !sensors.length) return null;
  return sensors.find((sensor) => {
    if (!sensor || !sensor.channelGroups) return false;
    return sensor.channelGroups.some((group) => {
      if (!group || !group.channelKey) return false;
      const { channelGroupType, channelGroupIndex } = parseChannelKey(group.channelKey) || {};

      return channelGroupType === 16 && channelGroupIndex === 0;
    });
  });
};

export const findInternalTempChannelGroupFromSensors = (sensors) => {
  if (!sensors || !sensors.length) {
    return null;
  }
  return sensors.reduce((found, sensor) => {
    if (found) return found;
    if (!sensor.channelGroups) return null;
    return sensor.channelGroups.find((group) => {
      if (!group || !group.channelKey) return false;
      const { channelGroupType, channelGroupIndex } = parseChannelKey(group.channelKey) || {};
      return channelGroupType === 16 && channelGroupIndex === 0;
    });
  }, null);
};

export const findInternalTempChannelGroupFromChannelGroups = (channelGroups) => {
  if (!channelGroups || !channelGroups.length) {
    return null;
  }
  return channelGroups.find((group) => {
    if (!group || !group.channelKey) return false;
    const parsedChannelGroupKey = parseChannelKey(group.channelKey);
    return parsedChannelGroupKey && parsedChannelGroupKey.channelGroupType === 16;
  });
};

export const isInternalMaxSensor = (channelKey) => {
  const { sensorMacAddress, deviceMacAddress } = parseChannelKey(channelKey);
  if (sensorMacAddress === -1 || deviceMacAddress === -1) {
    return false;
  }
  return sensorMacAddress === deviceMacAddress;
};

export const isWiredSensorAdapter = (channelGroups) => {
  if (!Array.isArray(channelGroups)) {
    return false;
  }
  // eslint-disable-next-line max-len
  const channelGroupTypes = channelGroups.map((group) => parseChannelKey(group.channelKey).channelGroupType);
  const defaultAdapterChannelGroups = [
    ChannelGroupType.BLUETOOTH_LE.id,
    ChannelGroupType.TEMPERATURE.id,
  ];
  return defaultAdapterChannelGroups.every((groupType) => channelGroupTypes.includes(groupType));
};

export const isBleSensor = (channelGroups) => {
  if (!Array.isArray(channelGroups)) {
    return false;
  }
  // eslint-disable-next-line max-len
  const channelGroupTypes = channelGroups.map((group) => parseChannelKey(group.channelKey).channelGroupType);
  return channelGroupTypes.includes(ChannelGroupType.BLUETOOTH_LE.id);
};

export const trimRaaChannelKey = (channelKey) => {
  const parts = channelKey.split('_');
  if (parts.length > 6) {
    return parts.slice(0, 6).join('_');
  }
  return channelKey;
};

export const getSensorKey = (channelKey) => `${parseChannelKey(channelKey).deviceMacAddress}_${parseChannelKey(channelKey).sensorMacAddress}`;

export const getDefaultAdapterPortLabel = (index) => {
  switch (index) {
    case adapterChannelGroupIndexTypes.INTERNAL:
      return 'Internal Temperature';
    case adapterChannelGroupIndexTypes.PORT1:
      return 'Port 1';
    case adapterChannelGroupIndexTypes.PORT2:
      return 'Port 2';
    case adapterChannelGroupIndexTypes.SWITCH:
      return 'Dry Contact';
    default:
      return `Port ${index}`;
  }
};

export const normalizeRaaChannelKey = (channelKey, subscriptionId) => {
  const parts = channelKey.split('_');

  if (parts.length === 6) {
    // Just append the subscription ID
    return `${channelKey}_${subscriptionId}`;
  }

  if (parts.length === 7) {
    const lastPart = parts[6];

    const isMatch = lastPart === String(subscriptionId);
    const isNotZero = lastPart !== '0';

    if (isMatch && isNotZero) {
      // Already correct, return as is
      return channelKey;
    }
    // Replace the last part with the subscription ID
    parts[6] = String(subscriptionId);
    return parts.join('_');
  }

  // Not 5 or 6 parts, return original string or throw error
  return channelKey;
};

export const getChannelDisplayData = (channel, deviceList, sensorMap) => {
  // pass channel object or channelKey
  const key = channel.channelKey || channel;
  const {
    deviceMacAddress,
    sensorMacAddress,
    channelGroupType,
    channelGroupIndex,
    channelType,
    channelIndex,
  } = parseChannelKey(key);

  const group = ChannelService.getChannelGroupTypeByValueId(channelGroupType);
  const groupName = (group && group.displayName) || 'Unknown Sensor Name';

  const port = ChannelService.getChannelGroupPortName(channelGroupType, channelGroupIndex);
  const channelObj = ChannelService.getChannelGroupChannel(
    channelGroupType, channelIndex, channelType,
  );
  const channelName = (channelObj && channelObj.displayName) || 'Unknown Channel Name';
  const device = deviceList.find((d) => d.uuid === deviceMacAddress);
  const deviceName = (device && device.name) || 'Unknown Device';
  const isRAMAXDevice = device && device.type === 'RAMAX';
  const channelGroupList = sensorMap[deviceMacAddress] || [];
  let maxComponentLabel = '';
  if (isRAMAXDevice) {
    maxComponentLabel = getMaxComponentLabel(channelGroupList, {
      macAddress: deviceMacAddress,
      channelGroupMacAddress: sensorMacAddress,
      channelGroupType,
      channelGroupIndex,
      channelType,
      channelIndex,
    });
  }

  const channelGroupIdentifier = `${sensorMacAddress}_${channelGroupType}_${channelGroupIndex}`;
  const targetSensor = channelGroupList.find(
    (sensor) => `${sensor.channelGroupMacAddress}_${sensor.channelGroupTypeId}_${sensor.channelGroupIndex}`
          === channelGroupIdentifier,
  );
  // if cant find user defined sensor name fallback to default
  const sensorName = (targetSensor && targetSensor.label)
    ? targetSensor.label : groupName;

  return {
    key,
    deviceName,
    // user defined
    sensorName,
    port,
    groupName,
    channelName,
    isRAMAXDevice,
    maxComponentLabel,
  };
};

export const filterHiddenChannelGroups = (
  channelGroups, hiddenChannelGroups, fieldMap,
) => channelGroups.filter((group) => {
  const isHidden = hiddenChannelGroups.some((hiddenGroup) => Object
    .entries(fieldMap).every(([groupField, hiddenField]) => {
      const groupValue = group[groupField];
      const hiddenValue = hiddenGroup[hiddenField];
      if (groupValue === undefined || hiddenValue === undefined) return false;

      return groupValue === hiddenValue;
    }));

  return !isHidden;
});

export const getUniqueChannelGroupListFromKeys = (channelKeyList) => {
  const uniqueChannelGroups = new Set();
  channelKeyList.forEach((key) => {
    const { channelGroupType, channelGroupIndex } = parseChannelKey(key);
    uniqueChannelGroups.add(`${channelGroupType}_${channelGroupIndex}`);
  });
  return uniqueChannelGroups;
};

export const getAlertableChannelList = (channels) => {
  const excludedGroupTypes = [
    // ChannelGroupType.BLUETOOTH_LE.id,
    ChannelGroupType.SCS_ALARM.id,
    ChannelGroupType.SCS_FAN_CTRL_GROUP.id,
    ChannelGroupType.SCS_FAN_CTRL_META_DATA.id,
    ChannelGroupType.WIFI.id,
    ChannelGroupType.APP_CONNECTOR_CONNECTION.id,
    ChannelGroupType.THRESHOLD_RANGE.id,
    ChannelGroupType.ICMP_PING.id,
    ChannelGroupType.HTTP_PING.id,
    ChannelGroupType.MOTION.id,
  ];
  const channelKeys = channels.map((channel) => channel.channelKey);
  // get unique set of changroup types
  const uniqueChannelGroupTypes = [
    ...new Set(
      channelKeys.map((key) => parseChannelKey(key).channelGroupType),
    ),
  ];
  const filteredGroups = uniqueChannelGroupTypes.filter(
    (groupType) => !excludedGroupTypes.includes(groupType),
  );
  let channelList = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const groupType of filteredGroups) {
    const channelsForType = ChannelService
      .getChannelGroupTypeByValueId(Number(groupType))
      .channels.filter(
        (channel) => channel.isAlertable
                && (channel.appType === AppTypes.UNIVERSAL
                    || channel.appType === AppTypes.CLOUD)
                && channel.channelType !== ChannelType.ALARM,
      );
    channelList = channelList.concat(channelsForType);
  }
  const includedChannelTypes = channelList.map((channel) => channel.channelType.id);

  return channels
    .filter((ch) => includedChannelTypes.includes(parseChannelKey(ch.channelKey).channelType));
};

export const filterDevicesByChannelKeys = (devices, channelKeys) => {
  const macAddresses = channelKeys
    .map((key) => parseChannelKey(key).deviceMacAddress);
  return devices.filter((device) => macAddresses.includes(device.uuid));
};
